// Spreadsheets: people (one row each, relatives as names with what kind of link they are) and
// relationships (one row per link, for pivot tables and network tools). Opens in Excel,
// Numbers, and Google Sheets (UTF-8 with a byte-order mark so accents survive Excel).

import { ageOf, ageText } from '../model/countdown';
import { formatDate } from '../model/dates';
import { graphOf } from '../model/graph';
import { birthDate, deathDate, deathEvent, displayName, eventTitle, genderLabel, latestEvent, lifeStatus, nameToString, primaryName, sortedPeople, visibleNames } from '../model/people';
import type { Person, Relationship, Tree } from '../model/types';
import { homeRelations, linkedText, relativesOf } from './describe';
import type { ExportPrivacy } from './redact';

export function csvCell(v: string | number | undefined): string {
  const s = v === undefined ? '' : String(v);
  // A leading =, +, -, or @ would run as a formula in a spreadsheet; a quote mark keeps it text.
  // Only a plain negative number is left alone: "-1+1" is a formula too.
  const safe = /^[=+\-@\t\r]/.test(s) && !/^-\d+(\.\d+)?$/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) || /^\s|\s$/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function csv(cols: string[], rows: (string | number | undefined)[][]): string {
  const lines = [cols.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))];
  return '﻿' + lines.join('\r\n') + '\r\n';
}

/** `tree` should already be redacted; `o` only decides which columns appear at all. */
export function exportCsv(tree: Tree, o: ExportPrivacy, now = new Date()): string {
  const g = graphOf(tree);
  const home = homeRelations(tree);
  type Col = [string, (p: Person) => string | undefined];
  const list = (p: Person, which: keyof ReturnType<typeof relativesOf>) => relativesOf(tree, p.id)[which].map((l) => linkedText(tree, l)).join('; ');
  const cols: Col[] = [
    ['ID', (p) => p.id],
    ['Name', (p) => displayName(p)],
    ['Given names', (p) => primaryName(p)?.given],
    ['Surname', (p) => primaryName(p)?.surname],
    ['Nickname', (p) => primaryName(p)?.nickname],
    ['Other names', (p) => visibleNames(p).slice(1).map((n) => nameToString(n)).join('; ')],
  ];
  if (o.hiddenNames) cols.push(['Hidden names', (p) => p.names.filter((n) => !visibleNames(p).includes(n)).map((n) => nameToString(n)).join('; ')]);
  if (o.identity) cols.push(['Gender', (p) => (p.gender.identity === 'unknown' ? '' : genderLabel(p.gender))], ['Pronouns', (p) => p.pronouns]);
  cols.push(['Living', (p) => ({ living: 'Yes', deceased: 'No', unknown: 'Not recorded' })[lifeStatus(p)]]);
  if (o.dates) cols.push(['Born', (p) => formatDate(birthDate(p), true)]);
  if (o.places) cols.push(['Birthplace', (p) => p.events.find((e) => e.type === 'birth')?.place]);
  if (o.dates) cols.push(['Died', (p) => formatDate(deathDate(p), true)]);
  if (o.places) cols.push(['Death place', (p) => deathEvent(p)?.place]);
  if (o.dates)
    cols.push([
      'Age',
      (p) => {
        const a = ageOf(p, now);
        return a ? (a.atDeath ? `${ageText(a)} at death` : ageText(a)) : '';
      },
    ]);
  if (o.otherEvents) {
    cols.push(['Occupation', (p) => latestEvent(p, 'occupation')?.label]);
    if (o.places) cols.push(['Lives in', (p) => latestEvent(p, 'residence')?.place]);
    cols.push([
      'Other life events',
      (p) =>
        p.events
          // The current occupation and home have columns of their own; earlier ones are listed here.
          .filter((e) => !['birth', 'death', 'burial', 'cremation'].includes(e.type) && e !== latestEvent(p, 'occupation') && (!o.places || e !== latestEvent(p, 'residence')))
          .map((e) => [eventTitle(e), formatDate(e.date, true), e.place].filter(Boolean).join(', '))
          .join('; '),
    ]);
  }
  if (home) cols.push([home.label, (p) => home.of(p.id)]);
  cols.push(['Parents', (p) => list(p, 'parents')], ['Partners', (p) => list(p, 'partners')], ['Children', (p) => list(p, 'children')], ['Siblings', (p) => list(p, 'siblings')]);
  if (o.friendsAndGroups) {
    cols.push(['Friends and connections', (p) => list(p, 'connections')]);
    cols.push(['Groups', (p) => (g.groupsOf.get(p.id) ?? []).map((x) => {
      const role = x.members.find((m) => m.personId === p.id)?.role;
      return role ? `${x.name} (${role})` : x.name;
    }).join('; ')]);
  }
  if (o.tags) cols.push(['Tags', (p) => p.tags.join('; ')]);
  if (o.notes) cols.push(['Notes', (p) => p.notes]);
  return csv(
    cols.map(([h]) => h),
    sortedPeople(tree).map((p) => cols.map(([, f]) => f(p))),
  );
}

const PARENT_TYPE: Record<string, string> = { biological: 'Biological', adoptive: 'Adoptive', step: 'Step', foster: 'Foster', guardian: 'Guardian', chosen: 'Chosen', surrogate: 'Surrogate', donor: 'Donor', unknown: '' };
const PARTNER_TYPE: Record<string, string> = { married: 'Married', partner: 'Partners', 'civil-union': 'Civil union', domestic: 'Domestic partnership', engaged: 'Engaged', dating: 'Dating', other: 'Other' };
const PARTNER_STATUS: Record<string, string> = { current: 'Current', separated: 'Separated', divorced: 'Divorced', ended: 'Ended', widowed: 'Widowed' };
const SIBLING_TYPE: Record<string, string> = { unknown: '', full: 'Full', half: 'Half', adoptive: 'Adoptive', step: 'Step', foster: 'Foster', chosen: 'Chosen' };
const SOCIAL_VERB: Record<string, string> = { friend: 'friend of', 'close-friend': 'close friend of', 'best-friend': 'best friend of', 'family-friend': 'family friend of', mentor: 'mentor of', roommate: 'roommate of', colleague: 'colleague of', neighbor: 'neighbor of', other: 'connected to' };

/** "parent of", "partner of", … and the kind of link, for one relationship. */
function relWords(r: Relationship): { verb: string; type: string; status: string } {
  switch (r.kind) {
    case 'parent':
      return { verb: 'parent of', type: PARENT_TYPE[r.type] ?? '', status: '' };
    case 'partner':
      return { verb: 'partner of', type: PARTNER_TYPE[r.type] ?? '', status: PARTNER_STATUS[r.status] ?? '' };
    case 'sibling':
      return { verb: 'sibling of', type: SIBLING_TYPE[r.type] ?? '', status: '' };
    case 'social':
      return { verb: SOCIAL_VERB[r.type] ?? 'connected to', type: '', status: '' };
  }
}

/** One row per relationship (and per group membership), each reading "A — parent of — B". */
export function exportRelationshipsCsv(tree: Tree, o: ExportPrivacy): string {
  const name = (id: string) => displayName(tree.people[id]);
  const cols = ['Person', 'Relationship', 'Other person', 'Type', 'Status'];
  if (o.dates) cols.push('From', 'To');
  if (o.places) cols.push('Place');
  cols.push('Label');
  if (o.notes) cols.push('Notes');
  cols.push('Person ID', 'Other person ID');
  const order = ['parent', 'partner', 'sibling', 'social'];
  const rels = Object.values(tree.relationships)
    .filter((r) => tree.people[r.a] && tree.people[r.b])
    .sort((x, y) => order.indexOf(x.kind) - order.indexOf(y.kind) || name(x.a).localeCompare(name(y.a)) || name(x.b).localeCompare(name(y.b)));
  const rows: (string | undefined)[][] = rels.map((r) => {
    const w = relWords(r);
    const row: (string | undefined)[] = [name(r.a), w.verb, name(r.b), w.type, w.status];
    if (o.dates) row.push(formatDate(r.start, true), formatDate(r.end, true));
    if (o.places) row.push(r.place);
    row.push(r.label);
    if (o.notes) row.push(r.notes);
    row.push(r.a, r.b);
    return row;
  });
  if (o.friendsAndGroups)
    for (const grp of Object.values(tree.groups).sort((a, b) => a.name.localeCompare(b.name)))
      for (const m of grp.members) {
        if (!tree.people[m.personId]) continue;
        const row: (string | undefined)[] = [name(m.personId), 'member of', grp.name, 'Group', ''];
        if (o.dates) row.push('', '');
        if (o.places) row.push('');
        row.push(m.role);
        if (o.notes) row.push('');
        row.push(m.personId, grp.id);
        rows.push(row);
      }
  return csv(cols, rows);
}
